/** 探针默认超时：坏链接不能让上层交互永远挂起。 */
const PROBE_TIMEOUT_MS = 10_000;
/** 无有效元数据时各探针返回的统一空结果。 */
const EMPTY_METADATA = { durationMs: null, width: null, height: null };
/** 创建带超时的一次性结算器，探针事件与定时器都只清理一次。 */
function createProbeSettler(resolve) {
    /** 是否已结算。 */
    let settled = false;
    /** 结算前注册的定时器。 */
    const timerIds = [];
    /** 结算前注册的 DOM 监听。 */
    const listeners = [];
    /** 结算并清理所有探针资源。 */
    function settle(result) {
        if (settled)
            return;
        settled = true;
        timerIds.forEach((timerId) => {
            window.clearTimeout(timerId);
        });
        listeners.forEach(([target, eventName, handler]) => {
            target.removeEventListener(eventName, handler);
        });
        resolve(result);
    }
    return {
        settle,
        /** 注册超时定时器，超时后以给定结果结算。 */
        armTimeout(onTimeout, timeoutMs = PROBE_TIMEOUT_MS) {
            timerIds.push(window.setTimeout(onTimeout, timeoutMs));
        },
        /** 注册 DOM 监听，结算时自动解绑。 */
        once(target, eventName, handler) {
            target.addEventListener(eventName, handler);
            listeners.push([target, eventName, handler]);
        },
    };
}
/**
 * 用隐藏视频元素实测线上视频的时长与分辨率。
 * 服务端上传接口不回填 duration 与宽高，编辑器在素材入列时按 src 实测补齐。
 * @param src 视频可访问地址。
 * @returns 时长（毫秒）与像素宽高；无法读取时对应字段为 null。
 */
export function probeVideoMetadata(src) {
    return new Promise((resolve) => {
        /** 探测用视频元素，读到元数据即停，不进入播放也不挂载到文档。 */
        const video = document.createElement('video');
        /** 结算器统一管理超时与监听清理。 */
        const settler = createProbeSettler(resolve);
        settler.armTimeout(() => {
            settler.settle(EMPTY_METADATA);
        });
        settler.once(video, 'loadedmetadata', () => {
            /** 视频原生时长，直播流等场景可能是 Infinity。 */
            const seconds = video.duration;
            settler.settle({
                durationMs: Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 1000) : null,
                width: video.videoWidth > 0 ? video.videoWidth : null,
                height: video.videoHeight > 0 ? video.videoHeight : null,
            });
        });
        settler.once(video, 'error', () => {
            settler.settle(EMPTY_METADATA);
        });
        video.preload = 'metadata';
        video.src = src;
    });
}
/**
 * 实测线上视频时长，仅返回毫秒时长。
 * 画布 drop 等只需要时长的调用方使用；需要完整元数据请用 probeVideoMetadata。
 * @param src 视频可访问地址。
 * @returns 实测毫秒时长；无法读取时返回 null。
 */
export async function measureVideoDurationMs(src) {
    /** 完整探针结果中只取时长。 */
    const metadata = await probeVideoMetadata(src);
    return metadata.durationMs;
}
/**
 * 用隐藏音频元素实测线上音效时长。
 * @param src 音频可访问地址。
 * @returns 实测毫秒时长；无法读取时返回 null。
 */
export function probeAudioDurationMs(src) {
    return new Promise((resolve) => {
        /** 探测用音频元素，读到元数据即停。 */
        const audio = document.createElement('audio');
        /** 结算器统一管理超时与监听清理。 */
        const settler = createProbeSettler(resolve);
        settler.armTimeout(() => {
            settler.settle(null);
        });
        settler.once(audio, 'loadedmetadata', () => {
            /** 音频原生时长，流式资源可能是 Infinity。 */
            const seconds = audio.duration;
            settler.settle(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 1000) : null);
        });
        settler.once(audio, 'error', () => {
            settler.settle(null);
        });
        audio.preload = 'metadata';
        audio.src = src;
    });
}
/**
 * 用隐藏图片元素实测线上图片的原始分辨率。
 * @param src 图片可访问地址。
 * @returns 像素宽高；无法读取时返回 null。
 */
export function probeImageDimensions(src) {
    return new Promise((resolve) => {
        /** 探测用图片元素，读到自然尺寸即停。 */
        const image = document.createElement('img');
        /** 结算器统一管理超时与监听清理。 */
        const settler = createProbeSettler(resolve);
        settler.armTimeout(() => {
            settler.settle(null);
        });
        settler.once(image, 'load', () => {
            /** 图片自然尺寸即像素分辨率。 */
            const width = image.naturalWidth;
            const height = image.naturalHeight;
            settler.settle(width > 0 && height > 0 ? { durationMs: null, width, height } : null);
        });
        settler.once(image, 'error', () => {
            settler.settle(null);
        });
        image.src = src;
    });
}
